import CloseIcon from '@mui/icons-material/Close';
import { LoadingButton } from '@mui/lab';
import { Box, Button, Container, Grid, IconButton, MenuItem, Modal, SelectChangeEvent, Stack, Typography } from '@mui/material';
import { useFormik } from 'formik';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import countryList from 'react-select-country-list';
import styled from 'styled-components';
import * as ProjectHelper from '../api/project/projectHelper';
import { route } from '../route/Index';
import DropDown from '../shared/component/DropDown';
import Input from '../shared/component/Input';
import { ENodeEnv, EUnitOfMeasure } from '../shared/enum';
import { IProjectInfo } from '../shared/iProject';
import { dialogAsync } from '../shared/util';
import { EReduxState } from '../store/enum';
import { useAppDispatch, useAppSelector } from '../store/hook';
import { selectProject, setProject } from '../store/project/projectSlice';
import { selectUser } from '../store/user/userSlice';
import Map from './map/Map';
import UnitOfMeasureLogo from './UnitOfMeasureLogo.svg';

interface IInfoProps {}

const InfoContainer = styled(Container)`
    padding: 0;
    flex-grow: 1;
    margin-top: 1rem;
`;

type TProjectInfo = keyof IProjectInfo;

const style = {
    position: 'absolute' as 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 400,
};

export const title: TProjectInfo = 'title';
const projectNumber: TProjectInfo = 'projectNumber';
const client: TProjectInfo = 'client';
const companyName: TProjectInfo = 'companyName';
export const country: TProjectInfo = 'country';
const city: TProjectInfo = 'city';
const latitude: TProjectInfo = 'latitude';
const longitude: TProjectInfo = 'longitude';
const notes: TProjectInfo = 'notes';

const Info = (props: IInfoProps) => {
    const dispatch = useAppDispatch();
    const project = useAppSelector(selectProject);
    const user = useAppSelector(selectUser);

    const { t } = useTranslation();

    const [isLoadingMetric, setIsLoadingMetric] = React.useState(false);
    const [isLoadingImperial, setIsLoadingImperial] = React.useState(false);
    const [openModal, setOpenModal] = React.useState(false);

    const options = React.useMemo(() => countryList().getData(), []);

    const navigate = useNavigate();

    const handleOpenModal = () => {
        if (project.status == EReduxState.fulfilled && !project.data?.isBase) {
            dialogAsync('warning', t('areYouSure'), t('warningCreateNewProject'), () => setOpenModal(true));
        } else {
            setOpenModal(true);
        }
    };

    const handleCloseModal = () => setOpenModal(false);

    let initialValues: IProjectInfo = {
        authorEmail: user.data!.email,
        authorName: user.data!.firstName,
        authorSurname: user.data!.lastName,
        title: project?.data?.projectInfo?.title ? project?.data?.projectInfo?.title : process.env.NODE_ENV == ENodeEnv.Production ? '' : 'test',
        projectNumber: project?.data?.projectInfo?.projectNumber ? project?.data?.projectInfo?.projectNumber : '',
        client: project?.data?.projectInfo?.client ? project?.data?.projectInfo?.client : process.env.NODE_ENV == ENodeEnv.Production ? '' : 'test',
        companyName: project?.data?.projectInfo?.companyName ? project?.data?.projectInfo?.companyName : process.env.NODE_ENV == ENodeEnv.Production ? '' : 'test',
        country: project?.data?.projectInfo?.country ? project?.data?.projectInfo?.country : options.find(x => x.value == 'IT')!.label,
        city: project?.data?.projectInfo?.city ? project?.data?.projectInfo?.city : process.env.NODE_ENV == ENodeEnv.Production ? '' : 'test',
        latitude: project?.data?.projectInfo?.latitude ? project?.data?.projectInfo?.latitude : '',
        longitude: project?.data?.projectInfo?.longitude ? project?.data?.projectInfo?.longitude : '',
        notes: project?.data?.projectInfo?.notes ? project?.data?.projectInfo?.notes : process.env.NODE_ENV == ENodeEnv.Production ? '' : 'test',
    };

    const formik = useFormik<IProjectInfo>({
        initialValues,
        onSubmit: handleOpenModal,
    });

    const handleChangeProjectInfo = (event: React.ChangeEvent<any> | SelectChangeEvent<any>) => {
        let { name, value } = event.target;
        if (name == latitude || name == longitude || name == projectNumber) value = parseFloat(value);
        let updateValue = { [name]: value };
        formik.setValues({ ...formik.values, ...updateValue });
    };

    const handleClickNext = async (unitOfMeasure: EUnitOfMeasure) => {
        unitOfMeasure == EUnitOfMeasure.Metric ? setIsLoadingMetric(true) : setIsLoadingImperial(true);
        let payload = { ...project.data?.projectInfo, ...formik.values, unitOfMeasure, projectNumber: formik.values.projectNumber ? formik.values.projectNumber : undefined };
        let res = await ProjectHelper.composeProjectAsync(payload);
        dispatch(setProject(res.data));
        unitOfMeasure == EUnitOfMeasure.Metric ? setIsLoadingMetric(false) : setIsLoadingImperial(false);
        navigate(route.geometry);
    };

    return (
        <>
            <InfoContainer maxWidth='md'>
                <Stack height={'100%'} direction='column' spacing={4}>
                    <form onSubmit={formik.handleSubmit}>
                        <Stack flexGrow={1}>
                            <Grid container justifyContent='center' spacing={2}>
                                <Grid item md={6}>
                                    <Stack direction={'column'}>
                                        <Typography variant='h2' gutterBottom>
                                            {t('projectDetails')}
                                        </Typography>
                                        <Stack direction={'row'} justifyContent={'space-between'} spacing={1}>
                                            <Input required value={formik.values.title} id={title} name={title} label={t(title)} onChange={handleChangeProjectInfo} />
                                            <Input isNumber value={formik.values.projectNumber} id={projectNumber} label={t(projectNumber)} name={projectNumber} onChange={handleChangeProjectInfo} />
                                        </Stack>
                                        <Input required value={formik.values.client} id={client} label={t(client)} name={client} onChange={handleChangeProjectInfo} />
                                        <Typography style={{ paddingTop: '1rem' }} variant='h2' gutterBottom>
                                            {t('companyDetails')}
                                        </Typography>
                                        <Input required value={formik.values.companyName} id={companyName} label={t(companyName)} name={companyName} onChange={handleChangeProjectInfo} />
                                        <DropDown
                                            required
                                            label={t(country)}
                                            labelId={`${country}-label`}
                                            id={country}
                                            name={country}
                                            value={formik.values.country}
                                            onChange={e => handleChangeProjectInfo(e)}>
                                            {options.map(x => (
                                                <MenuItem key={x.label} value={x.label}>
                                                    {x.label}
                                                </MenuItem>
                                            ))}
                                        </DropDown>
                                        <Input required value={formik.values.city} id={city} label={t(city)} name={city} onChange={handleChangeProjectInfo} />
                                        <Typography variant='subtitle1' gutterBottom>
                                            {t('requiredFiled')}
                                        </Typography>
                                    </Stack>
                                </Grid>
                                <Grid item md={6}>
                                    <Stack direction={'column'}>
                                        <Typography variant='h2' gutterBottom>
                                            {t('location')}
                                        </Typography>
                                        <Stack direction={'row'} justifyContent={'space-between'} gap={1}>
                                            <Input isNumber required value={formik.values.latitude} name={latitude} id={latitude} label={t(latitude)} onChange={handleChangeProjectInfo} />
                                            <Input isNumber required value={formik.values.longitude} name={longitude} id={longitude} label={t(longitude)} onChange={handleChangeProjectInfo} />
                                        </Stack>
                                        <Typography variant='subtitle1'>{t('clickOnTheMapToSetThePin')}</Typography>
                                        <Map
                                            coordinates={{ latitude: formik.values.latitude, longitude: formik.values.longitude }}
                                            onClick={coordinate => {
                                                formik.setFieldValue(latitude, coordinate.latitude);
                                                formik.setFieldValue(longitude, coordinate.longitude);
                                            }}
                                        />
                                        <Input id={notes} name={notes} value={formik.values.notes} label={t(notes)} onChange={handleChangeProjectInfo} />
                                    </Stack>
                                </Grid>
                            </Grid>
                        </Stack>
                        <Stack alignItems='end'>
                            <Button variant='orange' type='submit'>
                                {t('next')}
                            </Button>
                        </Stack>
                    </form>
                </Stack>
            </InfoContainer>
            <Modal open={openModal} onClose={handleCloseModal} aria-labelledby='modal-soil-title' aria-describedby='modal-soil-description'>
                <Box mx={style}>
                    <Stack variant='modal' style={{ minHeight: '2rem' }} height='100%' direction='column' spacing={3} justifyContent={'center'} alignItems={'center'}>
                        <Stack alignSelf={'end'}>
                            <IconButton aria-label='close-modal' onClick={handleCloseModal}>
                                <CloseIcon />
                            </IconButton>
                        </Stack>
                        <img width={'100px'} src={UnitOfMeasureLogo} alt='UnitOfMeasureLogo' />
                        <Typography variant='h6'>{t('chooseUnitOfMeasure')}</Typography>
                        <Stack direction={'row'} spacing={2}>
                            <LoadingButton loading={isLoadingMetric} style={{ overflow: 'hidden' }} variant='orange' onClick={() => handleClickNext(EUnitOfMeasure.Metric)}>
                                {t('metric')}
                            </LoadingButton>
                            <LoadingButton loading={isLoadingImperial} style={{ overflow: 'hidden' }} variant='orange' onClick={() => handleClickNext(EUnitOfMeasure.Imperial)}>
                                {t('imperial')}
                            </LoadingButton>
                        </Stack>
                    </Stack>
                </Box>
            </Modal>
        </>
    );
};

export default Info;
